// The line above every page: where you are (the breadcrumbs) on the left, who
// you are on the right — avatar, name and role, opening the account menu. On a
// phone it also carries the ☰ that opens the sidebar, which is off-screen there.
//
// Drawn as a line, not a bar: the page's own background and a hairline
// underneath. It used to be a 64px white bar that, for staff, held only an
// avatar, with the breadcrumbs below it in the page; on 2026-10-01 it was
// removed on desktop, then brought back the same day as this line, taking the
// signed-in user's row from the bottom of the sidebar.
import React, { useState } from 'react';
import { Box, ButtonBase, IconButton, Skeleton, Typography } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';

import useResponsive from '@shared/hooks/useResponsive';
import { useAuth } from '@app/providers/AuthProvider';
import UserAvatar from './Avatar';
import PageBreadcrumbs from './PageBreadcrumbs';
import AccountMenu, { fullName, primaryRoleName } from './AccountMenu';
import { TOP_BAR_HEIGHT, frameLine } from './frame';

function AccountButton({ user, compact }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const name = fullName(user);
  const role = primaryRoleName(user);

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={`Account: ${name}`}
        aria-haspopup="menu"
        aria-expanded={anchorEl ? 'true' : undefined}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 0.5, pr: compact ? 0.5 : 1, borderRadius: 2, textAlign: 'left', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <UserAvatar name={name} size={36} />
        {!compact && (
          <Box sx={{ minWidth: 0, maxWidth: 200 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>{name}</Typography>
            {role && <Typography variant="caption" component="div" noWrap sx={{ color: 'text.secondary', lineHeight: 1.3 }}>{role}</Typography>}
          </Box>
        )}
      </ButtonBase>
      <AccountMenu anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
    </>
  );
}

const NavBar = ({ handleDrawerToggle }) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();

  return (
    <Box
      component="header"
      sx={(theme) => ({
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
        // Fixed, and the sidebar header's height too, so the two hairlines
        // meet; a bar that grew would step its line below the sidebar's.
        height: TOP_BAR_HEIGHT,
        px: { xs: 1.5, sm: 3 },
        borderBottom: frameLine(theme),
      })}
    >
      {isMobile && (
        <IconButton color="inherit" edge="start" aria-label="Open menu" onClick={handleDrawerToggle}>
          <MenuIcon />
        </IconButton>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <PageBreadcrumbs />
      </Box>
      {user ? <AccountButton user={user} compact={isMobile} /> : <Skeleton variant="circular" width={36} height={36} />}
    </Box>
  );
};

export default NavBar;
